import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CircularProgress from '@mui/material/CircularProgress'
import { CapeeshColors } from '../../assets/ColorPalette'
import { userService } from '../../_state/services'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme) => ({
    submissionCard: {
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        backgroundColor: '#FAFAFA',
    },
    userText: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#35323C',
        whiteSpace: 'pre-wrap',
        padding: '12px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E8E8',
        borderRadius: '6px',
        marginBottom: '12px',
    },
    scoreText: {
        fontFamily: 'Rubik',
        fontWeight: 600,
        fontSize: '14px',
        marginBottom: '8px',
    },
    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        color: '#555',
        lineHeight: '1.5',
        marginBottom: '8px',
    },
    feedbackItem: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        lineHeight: '1.4',
        padding: '4px 8px',
        marginBottom: '4px',
        borderRadius: '4px',
    },
    feedbackPositive: {
        backgroundColor: '#E8F5E9',
        color: '#2E7D32',
    },
    feedbackNegative: {
        backgroundColor: '#FFF3E0',
        color: '#E65100',
    },
    dateText: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: '#999',
        marginBottom: '8px',
    },
    cefrBadge: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontFamily: 'Rubik',
        fontSize: '12px',
        fontWeight: 500,
        marginLeft: '8px',
    },
    cefrAppropriate: {
        backgroundColor: '#E8F5E9',
        color: '#2E7D32',
    },
    cefrNotAppropriate: {
        backgroundColor: '#FFF3E0',
        color: '#E65100',
    },
    sectionLabel: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '12px',
        color: '#999',
        textTransform: 'uppercase',
        marginBottom: '4px',
    },
    emptyState: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        color: '#999',
        textAlign: 'center',
        padding: '32px',
    },
}))

const WritingSubmissionsModal = ({ open, onClose, organizationId, userId, customLessonId, lessonName }) => {
    const classes = useStyles()
    const [submissions, setSubmissions] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!open) return
        setLoading(true)
        setError(null)

        userService
            .getWritingSubmissions({ organizationId, userId, customLessonId })
            .then((response) => {
                setSubmissions(response.data || [])
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to load writing submissions', err)
                setError('Failed to load writing submissions')
                setLoading(false)
            })
    }, [open, organizationId, userId, customLessonId])

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const getScoreColor = (score) => {
        if (score >= 0.7) return '#2E7D32'
        if (score >= 0.4) return '#E65100'
        return '#C62828'
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #E0E0E0',
                }}>
                <Typography
                    style={{
                        fontFamily: 'Rubik',
                        fontWeight: 600,
                        fontSize: '18px',
                        color: '#35323C',
                    }}>
                    <FormattedMessage
                        id="course.modal.writing.submissions.title"
                        defaultMessage="Writing submissions - {lessonName}"
                        values={{ lessonName: lessonName || '' }}
                    />
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CancelRoundedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: '24px', minHeight: '200px' }}>
                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                        <CircularProgress />
                    </div>
                )}

                {!loading && error && (
                    <Typography className={classes.emptyState} style={{ color: '#C62828' }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && submissions.length === 0 && (
                    <Typography className={classes.emptyState}>
                        <FormattedMessage
                            id="course.modal.writing.submissions.empty"
                            defaultMessage="No writing submissions found."
                        />
                    </Typography>
                )}

                {!loading &&
                    !error &&
                    submissions.map((submission, index) => (
                        <div key={submission.id} className={classes.submissionCard}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                }}>
                                <Typography className={classes.dateText}>{formatDate(submission.createdAt)}</Typography>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        className={classes.scoreText}
                                        style={{ color: getScoreColor(submission.normalizedScore) }}>
                                        {(submission.normalizedScore * 100).toFixed(0)}%
                                    </Typography>
                                    <span
                                        className={`${classes.cefrBadge} ${submission.cefrAppropriate ? classes.cefrAppropriate : classes.cefrNotAppropriate}`}>
                                        {submission.cefrAppropriate ? 'CEFR OK' : 'Below CEFR'}
                                    </span>
                                </div>
                            </div>

                            <Typography className={classes.sectionLabel}>
                                <FormattedMessage
                                    id="course.modal.writing.submissions.studenttext"
                                    defaultMessage="Student's text"
                                />
                            </Typography>
                            <div className={classes.userText}>{submission.userText}</div>

                            {submission.feedback && (
                                <>
                                    <Typography className={classes.sectionLabel}>
                                        <FormattedMessage
                                            id="course.modal.writing.submissions.aifeedback"
                                            defaultMessage="AI Feedback"
                                        />
                                    </Typography>
                                    <Typography className={classes.feedbackText}>{submission.feedback}</Typography>
                                </>
                            )}

                            {submission.feedbackItems && submission.feedbackItems.length > 0 && (
                                <>
                                    <Typography className={classes.sectionLabel} style={{ marginTop: '8px' }}>
                                        <FormattedMessage
                                            id="course.modal.writing.submissions.details"
                                            defaultMessage="Details"
                                        />
                                    </Typography>
                                    {submission.feedbackItems.map((item, i) => (
                                        <div
                                            key={i}
                                            className={`${classes.feedbackItem} ${item.isPositive ? classes.feedbackPositive : classes.feedbackNegative}`}>
                                            {item.isPositive ? '+ ' : '- '}
                                            {item.text}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ))}
            </DialogContent>
        </Dialog>
    )
}

export default WritingSubmissionsModal
